import React from "react";
import { observer, inject } from "mobx-react";
import ListSearch from "../bucket/ListSearch";
import { Link } from "react-router-dom";
import { CubeTypeGroup } from "./CubeTypeGroup";
import { CustomTooltip } from "../../components/tooltip/CustomTooltip";
import { StoreContext } from "../../contexts/StoreContext";
import MaintenanceAgencyLabel from "./MaintenanceAgencyLabel";

@inject("detailViewStore", "previewStore", "popupStore")
@observer
export class RelatedFrameworksWithCubes extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
    };
  }

  componentDidMount() {
    this.context.setSearchMatrix(this.props.searchMatrixKey, "");
  }

  handleCollapse = () => {
    this.setState({
      open: !this.state.open,
    });
  };

  getCubes = () => {
    const { navigationLookupBuckets } = this.props;
    const { getEntityData, groupCubesByValidity } = this.context;
    let cubes = [];

    if (
      getEntityData.item.object.type === "Member" ||
      getEntityData.item.object.type === "Variable"
    ) {
      const cubeLists = { ...getEntityData.item.otherInformation };

      if (cubeLists && cubeLists.cubesReachableDirectly) {
        const filteredCubes = Object.values(
          groupCubesByValidity(cubeLists.cubesReachableDirectly, "code")
        ).flat();
        cubes = cubes.concat(filteredCubes);
      }
      if (cubeLists && cubeLists.cubesReachableFromChildren) {
        cubes = cubes.concat(cubeLists.cubesReachableFromChildren);
      }
      if (cubeLists && cubeLists.cubesReachableFromParents) {
        cubes = cubes.concat(cubeLists.cubesReachableFromParents);
      }
      // remove duplicates
      cubes = cubes.filter(
        (cube, index, array) =>
          array.findIndex((t) => t.tid === cube.tid) === index
      );
    } else {
      cubes =
        navigationLookupBuckets &&
        navigationLookupBuckets.Cubes &&
        navigationLookupBuckets.Cubes.objects
          ? navigationLookupBuckets.Cubes.objects
          : [];
    }
    return cubes;
  };

  getCubeConnections = () => {
    const { getEntityData, groupCubesByValidity } = this.context;
    const cubeLists = { ...getEntityData.item.otherInformation };
    const cubeConnections = { direct: [], children: [], parents: [] };

    if (cubeLists && cubeLists.cubesReachableDirectly) {
      const filteredCubes = Object.values(
        groupCubesByValidity(cubeLists.cubesReachableDirectly, "code")
      ).flat();
      cubeConnections.direct = filteredCubes.map((cube) => cube.tid);
    }
    if (cubeLists && cubeLists.cubesReachableFromChildren) {
      cubeConnections.children = cubeLists.cubesReachableFromChildren.map(
        (cube) => cube.tid
      );
    }
    if (cubeLists && cubeLists.cubesReachableFromParents) {
      cubeConnections.parents = cubeLists.cubesReachableFromParents.map(
        (cube) => cube.tid
      );
    }
    return cubeConnections;
  };

  getCubesForFramework = (framework) => {
    const cubes = this.getCubes();
    return cubes
      .filter((cube) => cube.frameworkId === framework.frameworkId)
      .sort();
  };

  getCubesWithCubeHierarchy = (cubeType, framework) => {
    const { getEntityData } = this.context;
    const cubeLists = { ...getEntityData.item.otherInformation };
    let results = [];
    if (
      cubeLists &&
      cubeLists[cubeType] &&
      cubeLists[cubeType].length &&
      cubeLists[cubeType][0]
    ) {
      results = Object.entries(cubeLists[cubeType][0])
        .map(([key, value]) => {
          return {
            type: key,
            items: framework
              ? value.filter(
                  (cubeHierarchy) =>
                    cubeHierarchy.sddCubeHierarchy.frameworkId ===
                    framework.frameworkId
                )
              : value,
          };
        })
        .filter((item) => item.items.length > 0);
    }
    return results ? results : [];
  };

  getTotalCubesNumber() {
    const { getEntityData } = this.context;
    const cubeLists = { ...getEntityData.item.otherInformation };
    let sum = 0;
    let cubeTypeArray = [
      "cubeHierarchyMap",
      "cubeChildrenHierarchyMap",
      "cubeParentHierarchyMap",
    ];
    cubeTypeArray.forEach((cubeType) => {
      if (cubeLists && cubeLists[cubeType] && cubeLists[cubeType][0]) {
        Object.entries(cubeLists?.[cubeType]?.[0]).forEach(([key, value]) => {
          value.map((item) => (sum += item.sddCubes.length));
        });
      }
    });
    return sum + this.getCubes().length;
  }

  // currently we sort frameworks by MA_ID and CODE, although this can change (e.g. preferred could be MA of root item)
  fnSortFrameworkByMaIdAndCodeWithEcbFirst = function (f1, f2) {
    let byMa =
      f1.maintenanceAgencyId === "ECB" && f2.maintenanceAgencyId !== "ECB"
        ? -1
        : f1.maintenanceAgencyId !== "ECB" && f2.maintenanceAgencyId === "ECB"
        ? 1
        : f1.maintenanceAgencyId.localeCompare(f2.maintenanceAgencyId);
    return byMa === 0 ? f1.code.localeCompare(f2.code) : byMa;
  };

  getCubeHierarchiesFilteredLength = (cubesWithHierarchyFiltered) => {
    return (
      cubesWithHierarchyFiltered &&
      cubesWithHierarchyFiltered.length > 0 &&
      cubesWithHierarchyFiltered.reduce(
        (prevCount, currentCubeType) =>
          prevCount +
          currentCubeType.items.reduce((x, y) => {
            return x + y.sddCubes.length;
          }, 0),
        0
      )
    );
  };
  static contextType = StoreContext;
  render() {
    const { navigationLookupBuckets, searchMatrixKey } = this.props;
    const { getEntityData, fetchingDataStatus } = this.context;
    const { clearEntityData } = this.props.previewStore;
    let frameworks = [];

    if (
      getEntityData.item.object.type === "Member" ||
      getEntityData.item.object.type === "Variable"
    ) {
      const frameworkLists = { ...getEntityData.item.otherInformation };

      if (frameworkLists && frameworkLists.frameworksReachableDirectly) {
        frameworks = frameworks.concat(
          frameworkLists.frameworksReachableDirectly
        );
      }
      if (frameworkLists && frameworkLists.frameworksReachableFromChildren) {
        frameworks = frameworks.concat(
          frameworkLists.frameworksReachableFromChildren
        );
      }
      if (frameworkLists && frameworkLists.frameworksReachableFromParents) {
        frameworks = frameworks.concat(
          frameworkLists.frameworksReachableFromParents
        );
      }
      // remove duplicates
      frameworks = frameworks.filter(
        (cube, index, array) =>
          array.findIndex((t) => t.tid === cube.tid) === index
      );
    } else {
      frameworks =
        navigationLookupBuckets &&
        navigationLookupBuckets.Frameworks &&
        navigationLookupBuckets.Frameworks.objects
          ? navigationLookupBuckets.Frameworks.objects
          : [];
    }

    const searchExpr = this.context.getSearchMatrix[searchMatrixKey] || "";

    const cubeConnections = this.getCubeConnections();
    const totalCubesNumber = this.getTotalCubesNumber();

    const cubesWithHierarchy =
      this.getCubesWithCubeHierarchy("cubeHierarchyMap");
    const childrenCubesWithHierarchy = this.getCubesWithCubeHierarchy(
      "cubeChildrenHierarchyMap"
    );
    const parentCubesWithHierarchy = this.getCubesWithCubeHierarchy(
      "cubeParentHierarchyMap"
    );
    const cubesWithHierarchyFiltered =
      this.context.applyFiltersWithCubeHierarchy(
        cubesWithHierarchy,
        searchExpr
      );
    const childrenCubesWithHierarchyFiltered =
      this.context.applyFiltersWithCubeHierarchy(
        childrenCubesWithHierarchy,
        searchExpr
      );
    const parentCubesWithHierarchyFiltered =
      this.context.applyFiltersWithCubeHierarchy(
        parentCubesWithHierarchy,
        searchExpr
      );
    const cubesWithHierarchiesFilteredLength =
      this.getCubeHierarchiesFilteredLength(cubesWithHierarchyFiltered);
    const childrenCubesWithHierarchiesFilteredLength =
      this.getCubeHierarchiesFilteredLength(childrenCubesWithHierarchyFiltered);
    const parentCubesWithHierarchiesFilteredLength =
      this.getCubeHierarchiesFilteredLength(parentCubesWithHierarchyFiltered);

    const frameworkWithCubes = frameworks
      .sort(this.fnSortFrameworkByMaIdAndCodeWithEcbFirst)
      .map((framework) => {
        const cubes = this.getCubesForFramework(framework);
        const cubeHierarchyMap = this.getCubesWithCubeHierarchy(
          "cubeHierarchyMap",
          framework
        );
        const cubeChildrenHierarchyMap = this.getCubesWithCubeHierarchy(
          "cubeChildrenHierarchyMap",
          framework
        );
        const cubeParentHierarchyMap = this.getCubesWithCubeHierarchy(
          "cubeParentHierarchyMap",
          framework
        );

        return (
          <div className="framework-with-cubes" key={framework.name}>
            <div className="framework-header">
              <span className="framework-label">Framework: </span>
              <Link
                onClick={() => {
                  clearEntityData();
                }}
                // key="title-name"
                to={`/view/${framework.type}/${framework.tid}`}
                // className="object-name"
              >
                {framework.name}
              </Link>
              {`(${framework.code})`}
              <MaintenanceAgencyLabel
                maintenanceAgencyId={framework.maintenanceAgencyId}
              ></MaintenanceAgencyLabel>
            </div>

            <CubeTypeGroup
              key={framework.name}
              cubes={cubes}
              frameworks={frameworks}
              cubeConnections={cubeConnections}
              cubesWithHierarchy={cubeHierarchyMap}
              parentCubesWithHierarchy={cubeParentHierarchyMap}
              childrenCubesWithHierarchy={cubeChildrenHierarchyMap}
              searchExpr={searchExpr}
              getCubeHierarchiesFilteredLength={
                this.getCubeHierarchiesFilteredLength
              }
            />
          </div>
        );
      });
    return (
      <div className="related-objects custom-panel">
        <div className="custom-panel-heading">
          <h4>
            Related Cubes (
            {this.context.applyFilters(this.getCubes(), searchExpr).length +
              cubesWithHierarchiesFilteredLength +
              childrenCubesWithHierarchiesFilteredLength +
              parentCubesWithHierarchiesFilteredLength}
            /{totalCubesNumber})
            {!totalCubesNumber &&
              (fetchingDataStatus.get("Cubes") === true ||
                fetchingDataStatus.get("Frameworks") === true) && (
                <div className="lds-ring">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              )}
          </h4>
          <CustomTooltip type="cube" />
        </div>
        <div className="custom-panel-body related-types-list">
          <ListSearch
            searchMatrixKey={searchMatrixKey}
            placeholder={"Filter by Cube Name or Code"}
          />
          {frameworkWithCubes}
        </div>
      </div>
    );
  }
}
